import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { getIntakeFormResponse } from '../services/intakeFormService';
import Spinner from '../components/common/Spinner';
import AssignFormModal from '../components/features/intakeForms/AssignFormModal';

function renderAnswer(question, answers) {
  const val = answers?.[question.id];
  if (val === undefined || val === null || val === '') return <span className="text-neutral-400 italic">No answer</span>;
  if (val === true) return 'Yes';
  if (val === false) return 'No';
  if (Array.isArray(val)) return val.length > 0 ? val.join(', ') : <span className="text-neutral-400 italic">None selected</span>;
  return String(val);
}

export default function IntakeFormResponseViewer() {
  const { responseId } = useParams();
  const [isAssignOpen, setIsAssignOpen] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['intakeFormResponse', responseId],
    queryFn: () => getIntakeFormResponse(responseId),
  });

  if (isLoading) return <div className="flex justify-center py-20"><Spinner /></div>;
  if (isError) return <div className="text-center py-20 text-danger-600">Failed to load response.</div>;

  const { response } = data;
  const form = response.form;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to={`/intake-forms/${form.id}`} className="text-neutral-400 hover:text-neutral-600 transition-colors">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">{form.title}</h1>
            <p className="text-sm text-neutral-500 mt-0.5">
              {response.client
                ? `${response.client.firstName} ${response.client.lastName}`
                : '—'}
              {response.submittedAt && ` · submitted ${format(new Date(response.submittedAt), 'MMM d, yyyy h:mm a')}`}
            </p>
          </div>
        </div>
        <button onClick={() => setIsAssignOpen(true)} className="btn btn-secondary text-sm">
          Assign again
        </button>
      </div>

      {/* Score banner for auto-scored instruments */}
      {response.score != null && (
        <div className="card mb-6 border-l-4 border-primary-400 dark:border-primary-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-0.5">Total Score</p>
              <p className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">{response.score}</p>
            </div>
            {response.scoreInterpretation && (
              <div className="text-right">
                <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-0.5">Interpretation</p>
                <p className="text-sm font-medium text-neutral-800 dark:text-neutral-200">{response.scoreInterpretation}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Answers */}
      <div className="card mb-6 space-y-5">
        <h2 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide">Answers</h2>
        {(form.schema || []).map((question) => (
          <div key={question.id}>
            <p className="text-sm font-medium text-neutral-700 mb-1">
              {question.label}
              {question.required && <span className="text-danger-500 ml-1">*</span>}
            </p>
            <p className="text-sm text-neutral-900">{renderAnswer(question, response.answers)}</p>
          </div>
        ))}
      </div>

      {/* Signature */}
      {response.signatureData && (
        <div className="card mb-6">
          <h2 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide mb-4">Signature</h2>
          <div className="border border-neutral-200 rounded-lg p-2 inline-block">
            <img src={response.signatureData} alt="Client signature" className="max-h-24" />
          </div>
          <div className="mt-3 text-xs text-neutral-500 space-y-1">
            {response.signedAt && (
              <p>Signed: {format(new Date(response.signedAt), 'MMMM d, yyyy h:mm:ss a')}</p>
            )}
            {response.ipAddress && <p>IP address: {response.ipAddress}</p>}
          </div>
        </div>
      )}

      <AssignFormModal
        formId={form.id}
        formTitle={form.title}
        isOpen={isAssignOpen}
        onClose={() => setIsAssignOpen(false)}
      />
    </div>
  );
}
